import * as React from "react";
import { Controller } from "react-hook-form";
import type {
  Control,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import * as SelectPrimitive from "@radix-ui/react-select";
import { cn } from "@/shared/lib/utils";
import { ChevronDown, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Label } from "@/shared/ui/components/ui/label";

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface SelectBaseProps
  extends Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    "onChange" | "style"
  > {
  options: SelectOption[];
  placeholder?: string;
  error?: string;
  onChange?: (value: string) => void;
  value?: string;
  disabled?: boolean;
  label?: string;
  labelIcon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  innerLabel?: string;
  hint?: string;
  onErrorClear?: () => void;
  id?: string;
  open?: boolean; // Control open state from outside
  onOpenChange?: (open: boolean) => void; // Handle open state changes
  darkModeOnOpen?: boolean; // Apply dark styling when open
  containerClassName?: string;
  footerContent?: React.ReactNode; // Content rendered at the bottom of the dropdown
  rightText?: string;
}

export interface SelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<
  SelectBaseProps,
  "name" | "value" | "onChange" | "error" | "open" | "onOpenChange"
> {
  name?: TName;
  control?: Control<TFieldValues>;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  rightText?: string;
}

const SelectBase = React.forwardRef<HTMLButtonElement, SelectBaseProps>(
  (
    {
      className,
      containerClassName,
      options,
      placeholder,
      error,
      onChange,
      value,
      disabled,
      label,
      labelIcon,
      leftIcon,
      innerLabel,
      hint,
      onErrorClear,
      id,
      open: controlledOpen,
      onOpenChange: controlledOnOpenChange,
      darkModeOnOpen = false,
      footerContent,
      rightText,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const selectId = id || React.useId();
    const hintId = `${selectId}-hint`;
    const hasError = Boolean(error);
    const [internalOpen, setInternalOpen] = React.useState(false);

    // Use controlled state if provided, otherwise use internal state
    const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
    const setIsOpen = controlledOnOpenChange || setInternalOpen;

    const filteredOptions = options.filter((o) => o.value !== "");
    const hasOptions = filteredOptions.length > 0;
    const selectedOption = value ? filteredOptions.find((o) => o.value === value) : undefined;

    const handleValueChange = (newValue: string) => {
      if (hasError && onErrorClear) onErrorClear();
      onChange?.(newValue);
    };

    return (
      <div className="w-full">
        {label && (
          <Label
            htmlFor={selectId}
            className={cn(
              "mb-3 flex items-center gap-2 text-sm font-bold text-grey-900",
              disabled && "text-grey-200",
              hasError && "text-error-700"
            )}
          >
            <span>{label}</span>
            {labelIcon && <span>{labelIcon}</span>}
          </Label>
        )}

        <SelectPrimitive.Root
          value={value}
          onValueChange={handleValueChange}
          disabled={disabled}
          open={isOpen}
          onOpenChange={setIsOpen}
        >
          <div className="relative">
            {/* TRIGGER (peer) */}
            <SelectPrimitive.Trigger
              id={selectId}
              ref={ref}
              aria-invalid={hasError}
              aria-describedby={hint || error ? hintId : undefined}
              className={cn(
                "peer flex rtl:flex-row-reverse w-full items-center justify-between gap-2 whitespace-nowrap rounded-lg border bg-white py-4 pe-10 rtl:pe-5 rtl:ps-10 text-sm font-semibold transition-colors",

                leftIcon
                  ? "ps-12 ltr:pe-5 rtl:ps-5 rtl:pe-12"
                  : "px-5 rtl:pe-5",

                // Dark mode when open (optional)
                darkModeOnOpen &&
                isOpen &&
                "bg-grey-900 text-white border-grey-900",

                // Default state styles
                !(darkModeOnOpen && isOpen) && "border-grey-100",
                !(darkModeOnOpen && isOpen) && !hasError && "text-grey-000",
                !(darkModeOnOpen && isOpen) && hasError && "text-error-700",
                !(darkModeOnOpen && isOpen) &&
                !hasError &&
                "data-[placeholder]:text-grey-200 not-data-[placeholder]:text-grey-900",

                "hover:border-brand-green",
                "focus-visible:outline-none focus-visible:text-grey-900",
                "disabled:cursor-not-allowed disabled:bg-grey-50 disabled:text-grey-200",
                "aria-[invalid=true]:border-error-700 aria-[invalid=true]:bg-error-bg aria-[invalid=true]:text-error-700",
                className
              )}
              {...(props as any)}
            >
              <span className="flex items-center gap-1 overflow-hidden min-w-0">
                {innerLabel && (
                  <span
                    className={cn(
                      "font-medium shrink-0",
                      darkModeOnOpen && isOpen ? "text-white" : "text-grey-500"
                    )}
                  >
                    {innerLabel}:
                  </span>
                )}
                <span className="line-clamp-1">
                  <SelectPrimitive.Value placeholder={placeholder}>
                    {selectedOption ? (
                      <div className="flex items-center gap-2">
                        {selectedOption.icon && (
                          <span className="flex-shrink-0 w-[30px] h-[20px] flex items-center justify-center">
                            {selectedOption.icon}
                          </span>
                        )}
                        <span>{selectedOption.label}</span>
                      </div>
                    ) : undefined}
                  </SelectPrimitive.Value>
                </span>
              </span>

              <div className="flex items-center gap-2">
                {rightText && (
                  <span className="text-xxs text-grey-400 font-medium whitespace-nowrap">
                    {rightText}
                  </span>
                )}
                <SelectPrimitive.Icon asChild>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4",
                      darkModeOnOpen && isOpen ? "text-white" : "text-current"
                    )}
                  />
                </SelectPrimitive.Icon>
              </div>
            </SelectPrimitive.Trigger>

            {leftIcon && (
              <div
                className={cn(
                  "absolute start-5 top-1/2 -translate-y-1/2 pointer-events-none z-10 transition-colors",
                  darkModeOnOpen && isOpen && "text-white",
                  !(darkModeOnOpen && isOpen) && !hasError && "text-grey-900",
                  !(darkModeOnOpen && isOpen) && hasError && "text-error-700",
                  !(darkModeOnOpen && isOpen) &&
                  !hasError &&
                  "peer-data-[placeholder]:text-grey-200",
                  !(darkModeOnOpen && isOpen) &&
                  "peer-not-data-[placeholder]:text-grey-900",
                  !(darkModeOnOpen && isOpen) &&
                  "peer-focus-visible:text-grey-900",
                  "peer-disabled:text-grey-200",
                  "peer-aria-[invalid=true]:text-error-700"
                )}
              >
                {leftIcon}
              </div>
            )}
          </div>

          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              position="popper"
              className={cn(
                "relative z-50 overflow-hidden rounded-md border bg-white text-grey-900 ",
                "w-[var(--radix-select-trigger-width)]",
                containerClassName
              )}
            >
              <SelectPrimitive.Viewport className="p-1">
                {hasOptions ? (
                  filteredOptions.map((option) => (
                    <SelectPrimitive.Item
                      key={option.value}
                      value={option.value}
                      className={cn(
                        "relative flex rtl:flex-row-reverse w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none",
                        "focus:bg-grey-100",
                        "data-[disabled]:opacity-50"
                      )}
                    >
                      <SelectPrimitive.ItemIndicator className="absolute right-2">
                        <Check className="h-4 w-4" />
                      </SelectPrimitive.ItemIndicator>
                      <SelectPrimitive.ItemText>
                        <div className="flex items-center gap-2">
                          {option.icon && (
                            <span className="flex-shrink-0 w-[30px] h-[20px] flex items-center justify-center">
                              {option.icon}
                            </span>
                          )}
                          <span>{option.label}</span>
                        </div>
                      </SelectPrimitive.ItemText>
                    </SelectPrimitive.Item>
                  ))
                ) : (
                  <div className="px-2 py-1.5 text-sm text-grey-400">
                    {t("common.noOption")}
                  </div>
                )}
              </SelectPrimitive.Viewport>
              {footerContent && (
                <div
                  className="border-t border-grey-100"
                  onPointerDown={(e) => e.preventDefault()}
                >
                  {footerContent}
                </div>
              )}
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>

        {(hint || (error && error.trim())) && (
          <p
            id={hint ? hintId : undefined}
            className={cn(
              "mt-3 text-xs font-medium text-grey-300",
              disabled && "text-grey-200",
              hasError && "text-error-700"
            )}
          >
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);

SelectBase.displayName = "SelectBase";

const Select = React.forwardRef<
  HTMLButtonElement,
  SelectProps<FieldValues, FieldPath<FieldValues>>
>((props, ref) => {
  const { name, control, rules, error: errorProp, ...restProps } = props;

  if (control && name) {
    return (
      <Controller
        name={name as any}
        control={control as any}
        rules={rules as any}
        render={({ field, fieldState }) => (
          <SelectBase
            {...restProps}
            value={field.value ?? ""}
            onChange={field.onChange}
            error={fieldState.error?.message || errorProp}
            ref={ref}
          />
        )}
      />
    );
  }

  return <SelectBase {...restProps} error={errorProp} ref={ref} />;
});

Select.displayName = "Select";

export { Select };
